import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/lib/i18n";
import { encryptSecret } from "@/lib/crypto";
import { createSecret, healthCheck, translateApiError } from "@/lib/api";
import HeroSection from "@/components/home/HeroSection";
import FeaturesSection from "@/components/home/FeaturesSection";
import PasswordGeneratorPopover from "@/components/home/PasswordGeneratorPopover";
import OptionsPanel from "@/components/home/OptionsPanel";

const EMAIL_REGEX = /.+@.+\..+/;

function logDev(prefix, err) {
  if (process.env.NODE_ENV !== "production") {
    console.error(prefix, err);
  }
}

export default function Home() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const [secret, setSecret] = useState("");
  const [expiry, setExpiry] = useState("1d");
  const [oneTime, setOneTime] = useState(true);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [useNotify, setUseNotify] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);

  useEffect(() => {
    healthCheck()
      .then((h) => setEmailEnabled(!!h.email_enabled))
      .catch((err) => logDev("[health]", err));
  }, []);

  const canSubmit =
    secret.trim().length > 0 &&
    !submitting &&
    (!usePassword || password.length > 0) &&
    (!useNotify || EMAIL_REGEX.test(notifyEmail));

  const handleEncrypt = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { ciphertext, iv, key } = await encryptSecret(secret);
      const res = await createSecret({
        ciphertext,
        iv,
        has_password: usePassword,
        password_hash: usePassword ? password : null,
        expiry,
        one_time: oneTime,
        notify_email: useNotify ? notifyEmail : null,
        sender_language: lang,
      });
      navigate(`/created/${res.id}#${key}`, { state: { meta: res } });
    } catch (err) {
      logDev("[encrypt]", err);
      toast.error(translateApiError(t, err));
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, secret, usePassword, password, expiry, oneTime, useNotify, notifyEmail, lang, navigate, t]);

  return (
    <div className="relative">
      <HeroSection />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-8" data-testid="composer-section">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-black/10 dark:border-white/10">
          <div className="lg:col-span-2 p-6 md:p-10 border-b lg:border-b-0 lg:border-r border-black/10 dark:border-white/10">
            <div className="flex items-center justify-between mb-4">
              <label className="text-xs font-mono uppercase tracking-widest text-neutral-500">
                {"// " + t("createSecret")}
              </label>
              <div className="flex items-center gap-3">
                <PasswordGeneratorPopover
                  textareaRef={textareaRef}
                  secret={secret}
                  setSecret={setSecret}
                />
                <span data-testid="char-count" className="text-xs font-mono text-neutral-500 dark:text-neutral-600">
                  {secret.length} {t("charCount")}
                </span>
              </div>
            </div>
            <Textarea
              ref={textareaRef}
              data-testid="secret-textarea"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={t("placeholder")}
              className="min-h-[220px] md:min-h-[280px] bg-transparent border border-black/10 dark:border-white/10 focus:border-black/40 dark:focus:border-white/40 focus:ring-1 focus:ring-black/20 dark:focus:ring-white/20 resize-none font-mono text-sm md:text-base leading-relaxed"
            />
          </div>
          <OptionsPanel
            expiry={expiry}
            setExpiry={setExpiry}
            oneTime={oneTime}
            setOneTime={setOneTime}
            usePassword={usePassword}
            setUsePassword={setUsePassword}
            password={password}
            setPassword={setPassword}
            useNotify={useNotify}
            setUseNotify={setUseNotify}
            notifyEmail={notifyEmail}
            setNotifyEmail={setNotifyEmail}
            emailEnabled={emailEnabled}
            submitting={submitting}
            canSubmit={canSubmit}
            onSubmit={handleEncrypt}
          />
        </div>
      </section>
      <FeaturesSection />
    </div>
  );
}
